import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { QuantitativeDataTable, Lifecycle5State, TableColumn } from '../../components/common/QuantitativeDataTable';
import { formatPrice, formatPercent } from '../../shared/domain/formatters';
import { compareDecimalStrings, getDecimalSign } from '../../shared/domain/decimal';

export interface MacroQuoteItem {
  symbol: string;
  name?: string;
  asset_class: string;
  price: string | null;
  pct_change: string | null;
  quote_unit: string;
  currency: string;
  timestamp_utc: string;
  provider: string;
}

export interface MacroQuotesResponse {
  session: string;
  readiness: string;
  partial_degraded: boolean;
  total_instruments: number;
  quotes: MacroQuoteItem[];
}

export const MacroMarketView: React.FC = () => {
  const [quotes, setQuotes] = useState<MacroQuoteItem[]>([]);
  const [state, setState] = useState<Lifecycle5State>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [session, setSession] = useState<string>('GLOBAL_OPEN');

  const fetchMacroQuotes = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const res = await apiClient.get<MacroQuotesResponse>('/api/v1/market/macro/quotes');

      setSession(res.session || 'GLOBAL_OPEN');
      if (!res.quotes || res.quotes.length === 0) {
        setQuotes([]);
        setState('empty');
      } else {
        setQuotes(res.quotes);
        setState(res.readiness === 'STALE' ? 'stale' : 'fresh');
      }
    } catch (err: any) {
      setQuotes([]);
      setState('disconnected');
      setErrorMessage(err?.message || '无法获取跨资产宏观行情数据');
    }
  }, []);

  useEffect(() => {
    fetchMacroQuotes();
  }, [fetchMacroQuotes]);

  const columns: TableColumn<MacroQuoteItem>[] = [
    {
      key: 'symbol',
      title: '标的代码',
      width: '140px',
      sortable: true,
      sortValue: (r) => r.symbol,
      render: (r) => (
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
          {r.symbol}
        </span>
      ),
    },
    {
      key: 'asset_class',
      title: '大类资产',
      width: '130px',
      sortable: true,
      sortValue: (r) => r.asset_class,
      render: (r) => {
        let label = r.asset_class;
        let badgeColor = 'var(--text-muted)';
        if (r.asset_class === 'COMMODITY_FUTURES') {
          label = '商品期货';
          badgeColor = 'var(--color-cyan)';
        } else if (r.asset_class === 'COMMODITY_SPOT') {
          label = '现货黄金';
          badgeColor = 'var(--color-amber)';
        } else if (r.asset_class === 'FX') {
          label = '外汇汇率';
          badgeColor = 'var(--color-emerald)';
        } else if (r.asset_class === 'INDEX') {
          label = '宏观指数';
          badgeColor = 'var(--text-primary)';
        }
        return (
          <span
            style={{
              fontSize: '11px',
              padding: '2px 6px',
              borderRadius: '4px',
              background: 'var(--bg-base)',
              border: '1px solid var(--border-subtle)',
              color: badgeColor,
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      key: 'price',
      title: '最新价格 / 汇率',
      align: 'right',
      width: '140px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.price, b.price),
      render: (r) => {
        if (r.price === null || r.price === undefined) {
          return <span style={{ color: 'var(--text-muted)' }}>-- (离线)</span>;
        }
        return (
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            {formatPrice(r.price)}
          </span>
        );
      },
    },
    {
      key: 'quote_unit',
      title: '单位 / 币种',
      width: '100px',
      render: (r) => <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{r.quote_unit || r.currency}</span>,
    },
    {
      key: 'pct_change',
      title: '涨跌幅 (%)',
      align: 'right',
      width: '120px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.pct_change, b.pct_change),
      render: (r) => {
        if (r.pct_change === null || r.pct_change === undefined) {
          return <span style={{ color: 'var(--text-muted)' }}>--</span>;
        }
        const sign = getDecimalSign(r.pct_change);
        let color = 'var(--text-muted)';
        if (sign > 0) color = 'var(--color-bullish)';
        if (sign < 0) color = 'var(--color-bearish)';
        return (
          <span style={{ fontWeight: 600, color }}>
            {formatPercent(r.pct_change, { signed: true })}
          </span>
        );
      },
    },
    {
      key: 'provider',
      title: '行情源',
      align: 'center',
      width: '100px',
      render: (r) => <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{r.provider}</span>,
    },
  ];

  return (
    <div className="macro-market-view" style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {/* Header Banner */}
      <div
        className="macro-header-card"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          padding: '16px',
          background: 'var(--bg-card)',
          borderRadius: '8px',
          border: '1px solid var(--border-card)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <div className="bilingual-view-header">
          <div className="bilingual-header-top">
            <h2 className="bilingual-header-title-zh" style={{ fontSize: '18px', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
              🌐 2. 宏观与大宗商品走廊
            </h2>
          </div>
          <span className="bilingual-header-subtitle-en">GLOBAL MACRO & COMMODITIES CORRIDOR</span>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            权威 15 大跨资产宏观锚定 · 离线诚实展示 · 零伪造数值
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              background: 'var(--bg-base)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-secondary)',
            }}
          >
            时段: <strong>{session}</strong>
          </span>
          <button
            onClick={fetchMacroQuotes}
            style={{
              padding: '4px 12px',
              borderRadius: '6px',
              background: 'var(--btn-primary-bg)',
              color: 'var(--btn-primary-text)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            刷新走廊
          </button>
        </div>
      </div>

      {/* Main Table */}
      <QuantitativeDataTable<MacroQuoteItem>
        data={quotes}
        columns={columns}
        state={state}
        errorMessage={errorMessage}
        onRetry={fetchMacroQuotes}
        pageSize={15}
        rowKey={(r) => r.symbol}
        title="跨资产走廊报价列表 (15 Instruments)"
        emptyMessage="暂无宏观标的数据"
      />
    </div>
  );
};
